// =============================================================================
// TagOverflowList — compact tag cell for order tables
// -----------------------------------------------------------------------------
// Renders the first N tag chips inline; when more tags exist, a "+N" chip
// with a tag icon opens a dropdown (built on the shared DropdownMenu
// primitives — portal + fixed positioning, so it is never clipped by the
// table's overflow container) listing every tag, grouped when multiple
// groups are supplied (e.g. admin: Internal vs Store tags).
// =============================================================================

import { clsx } from 'clsx';

import {
  DropdownMenu,
  DropdownTrigger,
  DropdownContent,
  useDropdownMenuClose,
} from './DropdownMenu';

export interface TagGroup {
  /** Stable key ("internal", "store", …). */
  key: string;
  /** Section label in the dropdown — shown only when several groups exist. */
  label?: string;
  tags: string[];
  /** Chip tone: brand tint (internal/ops labels) or neutral (store tags). */
  tone?: 'brand' | 'neutral';
  /** Makes chips clickable (e.g. apply the tag as a list filter). */
  onTagClick?: (tag: string) => void;
}

export interface TagOverflowListProps {
  groups: TagGroup[];
  /** Chips rendered inline before collapsing into the dropdown. Default 1. */
  maxVisible?: number;
  className?: string;
}

const TONE_CLASSES: Record<NonNullable<TagGroup['tone']>, string> = {
  brand: 'border-brand-200 bg-brand-50 text-brand-700',
  neutral: 'border-border bg-surface-secondary text-text-secondary',
};

const TONE_HOVER: Record<NonNullable<TagGroup['tone']>, string> = {
  brand: 'hover:bg-brand-100',
  neutral: 'hover:border-brand-500 hover:text-brand-600',
};

function TagIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
      <line x1="7" y1="7" x2="7.01" y2="7" />
    </svg>
  );
}

interface FlatTag {
  tag: string;
  group: TagGroup;
}

function TagChip({ tag, group }: FlatTag) {
  // No-op outside a DropdownMenu; inside the overflow panel it dismisses
  // the panel after a chip applies its filter.
  const closeDropdown = useDropdownMenuClose();
  const tone = group.tone ?? 'neutral';
  const base = clsx(
    'inline-flex max-w-[140px] items-center rounded-full border px-1.5 py-0.5 text-[10px] font-medium leading-tight',
    TONE_CLASSES[tone],
  );
  if (!group.onTagClick) {
    return (
      <span className={base} title={tag}>
        <span className="truncate">{tag}</span>
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        group.onTagClick?.(tag);
        closeDropdown();
      }}
      title={`Filter by tag "${tag}"`}
      className={clsx(base, 'transition-colors', TONE_HOVER[tone])}
    >
      <span className="truncate">{tag}</span>
    </button>
  );
}

export function TagOverflowList({
  groups,
  maxVisible = 1,
  className,
}: TagOverflowListProps) {
  const flat: FlatTag[] = groups.flatMap((group) =>
    group.tags.map((tag) => ({ tag, group })),
  );
  const visible = flat.slice(0, maxVisible);
  const hiddenCount = flat.length - visible.length;
  const groupsWithTags = groups.filter((g) => g.tags.length > 0);

  if (flat.length === 0) {
    return <span className="text-text-tertiary">—</span>;
  }

  return (
    // Table rows navigate on click — keep every tag interaction local.
    <span
      className={clsx('inline-flex items-center gap-1', className)}
      onClick={(e) => e.stopPropagation()}
    >
      {visible.map((entry) => (
        <TagChip key={`${entry.group.key}-${entry.tag}`} {...entry} />
      ))}
      {hiddenCount > 0 && (
        <DropdownMenu align="left">
          <DropdownTrigger>
            <span
              title={`Show all ${flat.length} tags`}
              className="inline-flex items-center gap-0.5 rounded-full border border-border bg-white px-1.5 py-0.5 text-[10px] font-medium leading-tight text-text-secondary transition-colors hover:border-brand-500 hover:text-brand-600"
            >
              <TagIcon />
              +{hiddenCount}
            </span>
          </DropdownTrigger>
          <DropdownContent minWidth="min-w-[200px]" className="max-w-[280px] p-2">
            <div className="max-h-64 space-y-2 overflow-y-auto">
              {groupsWithTags.map((group) => (
                <div key={group.key}>
                  {groupsWithTags.length > 1 && group.label && (
                    <p className="mb-1 px-0.5 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
                      {group.label}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-1">
                    {group.tags.map((tag) => (
                      <TagChip key={tag} tag={tag} group={group} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </DropdownContent>
        </DropdownMenu>
      )}
    </span>
  );
}
